import { useQuery } from '@apollo/client';

import useCompanyContext from 'common/hooks/useCompanyContext';

import {
  CatalogItemsDocument,
  CatalogItemsQueryVariables,
} from './graphql/catalogItems.gql';

const CATALOG_ITEMS_PER_PAGE = 20;

interface Parameters
  extends Pick<Partial<CatalogItemsQueryVariables>, 'first' | 'filters'> {
  skip?: boolean;
}

export const useCatalogItemsQuery = ({
  filters = null,
  first = CATALOG_ITEMS_PER_PAGE,
  skip,
}: Parameters) => {
  const { companyProfileId } = useCompanyContext();

  if (!companyProfileId) {
    throw new Error('companyProfileId is undefined in useCatalogItemsQuery');
  }

  const {
    data,
    fetchMore: apolloFetchMore,
    networkStatus,
    ...rest
  } = useQuery(CatalogItemsDocument, {
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    skip,
    variables: {
      after: '',
      companyProfileId,
      filters,
      first,
    },
  });

  const catalogItems = data?.viewer.company.catalogItems.edges ?? [];
  const cursor = data?.viewer?.company?.catalogItems?.pageInfo?.endCursor;

  const fetchMore = async () => {
    await apolloFetchMore({
      variables: { after: cursor },
    });
  };

  return {
    catalogItems: catalogItems.map(({ node }) => node),
    fetchMore,
    networkStatus,
    pageInfo: data?.viewer?.company?.catalogItems?.pageInfo,
    ...rest,
  };
};

export type CatalogItem = NonNullable<
  NonNullable<
    Awaited<ReturnType<typeof useCatalogItemsQuery>>['catalogItems']
  >[number]
>;

export type CatalogItemsPageInfo = NonNullable<
  NonNullable<Awaited<ReturnType<typeof useCatalogItemsQuery>>['pageInfo']>
>;
